/**
 * IA Live Search
 * Fetches and displays search results as user types
 */

if (!customElements.get('ia-live-search')) {
  class IaLiveSearch extends HTMLElement {
    constructor() {
      super();
      this.cachedResults = {};
      this.isOpen = false;
      this.searchTimeout = null;
      this.currentSearchTerm = '';
    }

    connectedCallback() {
      this.drawer = this.closest('ia-dropdown');
      this.input = this.querySelector('[data-predictive-search-input]');
      this.clearBtn = this.querySelector('[data-search-clear]');
      this.form = this.querySelector('form');
      this.resultsContainer = this.querySelector('[data-predictive-search-results]');
      this.initialStateContainer = this.querySelector('[data-search-initial-state]');
      this.preloader = this.querySelector('[data-search-preloader]');
      this.statusElement = this.querySelector('[role="status"]');

      if (!this.input || !this.form) return;

      this.setupEventListeners();
    }

    setupEventListeners() {
      if (this.eventListenersAttached) return;

      // Form submit handler
      this.form.addEventListener('submit', this.onFormSubmit.bind(this));

      // Input handlers with debounce
      this.input.addEventListener('input', this.debounce(() => {
        this.onChange();
      }, 300));

      this.input.addEventListener('focus', this.onFocus.bind(this));

      // Clear button
      if (this.clearBtn) {
        this.clearBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.clearSearchField();
          return false;
        });
      }

      // Drawer open event
      this.drawer?.addEventListener('ia-drawer-opened', (e) => {
        if (e.detail.drawerId === this.drawer.id) {
          this.input.focus();
          if (!this.getQuery().length) {
            this.showInitialState();
          }
        }
      });

      this.eventListenersAttached = true;
    }

    debounce(fn, wait) {
      return (...args) => {
        clearTimeout(this.searchTimeout);
        this.searchTimeout = setTimeout(() => fn.apply(this, args), wait);
      };
    }

    getQuery() {
      return this.input ? this.input.value.trim() : '';
    }

    onChange() {
      const searchTerm = this.getQuery();

      if (!searchTerm.length) {
        this.input.classList.remove('has-content');

        if (this.clearBtn) {
          this.clearBtn.classList.remove('has-content');
        }

        this.showInitialState();
        return;
      }

      this.input.classList.add('has-content');

      if (this.clearBtn) {
        this.clearBtn.classList.add('has-content');
      }

      if (searchTerm !== this.currentSearchTerm) {
        this.currentSearchTerm = searchTerm;
        this.getSearchResults(searchTerm);
      }
    }

    onFormSubmit(event) {
      if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
        event.preventDefault();
      }
    }

    onFocus() {
      const searchTerm = this.getQuery();

      this.input.classList.add('has-content');

      if (!searchTerm.length) {
        this.showInitialState();
        return;
      }

      if (this.getAttribute('results') === 'true') {
        this.hideInitialState();
      } else {
        this.getSearchResults(searchTerm);
      }
    }

    getSearchResults(searchTerm) {
      const queryKey = searchTerm.replace(/ /g, "-").toLowerCase();

      // Show loading state
      this.preloader?.classList.add('is-active');

      this.setLiveRegionLoadingState();

      // Check cache
      if (this.cachedResults[queryKey]) {
        this.renderSearchResults(this.cachedResults[queryKey]);
        this.preloader?.classList.remove('is-active');
        return;
      }

      fetch(`/search/suggest?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[type]')}=product,collection,page&${encodeURIComponent('resources[limit]')}=10&${encodeURIComponent('resources[options][fields]')}=title,product_type,variants.title,vendor,tag,body&section_id=ia_ajax_search_results`)
        .then((response) => {
          if (!response.ok) {
            throw new Error(response.status);
          }
          return response.text();
        })
        .then(text => {
          const parsedHTML = new DOMParser().parseFromString(text, 'text/html');
          const sectionElement = parsedHTML.querySelector('#shopify-section-ia_ajax_search_results');
          const resultsMarkup = sectionElement?.innerHTML;

          if (resultsMarkup && searchTerm === this.currentSearchTerm) {
            this.cachedResults[queryKey] = resultsMarkup;
            this.renderSearchResults(resultsMarkup);
          } else {
            this.showInitialState();
          }
        })
        .catch(error => {
          console.error('Search error:', error);
          this.showInitialState();
        })
        .finally(() => {
          this.preloader?.classList.remove('is-active');
        });
    }

    renderSearchResults(resultsMarkup) {
      if (!this.resultsContainer) return;

      this.resultsContainer.innerHTML = resultsMarkup;
      this.setAttribute('results', 'true');
      this.hideInitialState();
      this.resultsContainer.classList.remove('ia-hidden');
      this.setLiveRegionResults();
      this.isOpen = true;
    }

    clearSearchField() {
      if (this.input) {
        this.input.value = '';
        this.input.classList.remove('has-content');
      }
      if (this.clearBtn) {
        this.clearBtn.classList.remove('has-content');
      }
      this.currentSearchTerm = '';
      this.drawer?.close();
      this.showInitialState();
    }

    showInitialState() {
      if (this.initialStateContainer) {
        this.initialStateContainer.classList.remove('ia-hidden');
      }
      if (this.resultsContainer) {
        this.resultsContainer.innerHTML = '';
        this.resultsContainer.classList.add('ia-hidden');
      }
      this.removeAttribute('results');
      this.isOpen = false;
    }

    hideInitialState() {
      if (this.initialStateContainer) {
        this.initialStateContainer.classList.add('ia-hidden');
      }
    }

    setLiveRegionLoadingState() {
      if (!this.statusElement) return;
      
      this.setLiveRegionText('Loading...');
      this.setAttribute('loading', 'true');
    }

    setLiveRegionResults() {
      if (!this.statusElement) return;

      this.removeAttribute('loading');
      const countElement = this.querySelector('[data-predictive-search-live-region-count-value]');
      if (countElement) {
        this.setLiveRegionText(countElement.textContent);
      }
    }

    setLiveRegionText(statusText) {
      if (!this.statusElement || !statusText) return;

      this.statusElement.setAttribute('aria-hidden', 'false');
      this.statusElement.textContent = statusText;

      setTimeout(() => {
        this.statusElement.setAttribute('aria-hidden', 'true');
      }, 1000);
    }
  }

  customElements.define('ia-live-search', IaLiveSearch);
}
